import { 
  CMSAPIClient, 
  APIResponse, 
  User, 
  Page, 
  PageFormData, 
  PageQueryParams,
  MediaFile,
  MediaUploadData,
  MediaQueryParams,
  UserFormData,
  CMSSettings,
  AutoRAGSettings,
  Dashboard,
  Template,
  NavigationItem
} from '../types/cms';

class MotoManualCMSAPI implements CMSAPIClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL?: string) {
    // Use production API URL when deployed, development proxy when local
    if (!baseURL) {
      if (window.location.hostname === 'admin.motomanual.ai') {
        this.baseURL = 'https://api.motomanual.ai/api';
      } else {
        this.baseURL = '/api'; // Uses Vite proxy during development
      }
    } else {
      this.baseURL = baseURL;
    }
    this.token = localStorage.getItem('cms_token');
  }

  private async request<T = any>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.token) {
      (headers as Record<string, string>).Authorization = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          ok: false,
          error: {
            code: data.error?.code || 'UNKNOWN_ERROR',
            message: data.error?.message || 'An unknown error occurred',
          },
        };
      }

      return {
        ok: true,
        data: data.data || data,
        meta: data.meta,
      };
    } catch (error) {
      return {
        ok: false,
        error: {
          code: 'NETWORK_ERROR',
          message: 'Failed to connect to the server',
        },
      };
    }
  }

  // Authentication
  async login(email: string, password: string): Promise<APIResponse<{ user: User; token: string }>> {
    const response = await this.request<{ user: User; token: string }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (response.ok && response.data) {
      this.token = response.data.token;
      localStorage.setItem('cms_token', this.token);
    }

    return response;
  }

  async logout(): Promise<APIResponse> {
    const response = await this.request('/auth/logout', { method: 'POST' });
    this.token = null;
    localStorage.removeItem('cms_token');
    return response;
  }

  async getCurrentUser(): Promise<APIResponse<User>> {
    return this.request<User>('/auth/me');
  }

  // Pages
  async getPages(params?: PageQueryParams): Promise<APIResponse<Page[]>> {
    const queryString = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return this.request<Page[]>(`/cms/pages${queryString}`);
  }

  async getPage(id: string): Promise<APIResponse<Page>> {
    return this.request<Page>(`/cms/pages/${id}`);
  }

  async createPage(data: PageFormData): Promise<APIResponse<Page>> {
    return this.request<Page>('/cms/pages', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePage(id: string, data: Partial<PageFormData>): Promise<APIResponse<Page>> {
    return this.request<Page>(`/cms/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePage(id: string): Promise<APIResponse> {
    return this.request(`/cms/pages/${id}`, { method: 'DELETE' });
  }

  async publishPage(id: string): Promise<APIResponse<Page>> {
    return this.request<Page>(`/cms/pages/${id}/publish`, { method: 'POST' });
  }

  // Media
  async getMediaFiles(params?: MediaQueryParams): Promise<APIResponse<MediaFile[]>> {
    const queryString = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return this.request<MediaFile[]>(`/cms/media${queryString}`);
  }

  async uploadMedia(data: MediaUploadData): Promise<APIResponse<MediaFile>> {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.alt) formData.append('alt', data.alt);
    if (data.caption) formData.append('caption', data.caption);

    return this.request<MediaFile>('/cms/media/upload', {
      method: 'POST',
      headers: this.token ? { Authorization: `Bearer ${this.token}` } : {},
      body: formData,
    });
  }

  async deleteMedia(id: string): Promise<APIResponse> {
    return this.request(`/cms/media/${id}`, { method: 'DELETE' });
  }

  // Users
  async getUsers(): Promise<APIResponse<User[]>> {
    return this.request<User[]>('/cms/users');
  }

  async createUser(data: UserFormData): Promise<APIResponse<User>> {
    return this.request<User>('/cms/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id: string, data: Partial<UserFormData>): Promise<APIResponse<User>> {
    return this.request<User>(`/cms/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id: string): Promise<APIResponse> {
    return this.request(`/cms/users/${id}`, { method: 'DELETE' });
  }

  // Settings
  async getSettings(): Promise<APIResponse<CMSSettings>> {
    return this.request<CMSSettings>('/cms/settings');
  }

  async updateSettings(data: Partial<CMSSettings>): Promise<APIResponse<CMSSettings>> {
    return this.request<CMSSettings>('/cms/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAutoRAGSettings(): Promise<APIResponse<AutoRAGSettings>> {
    return this.request<AutoRAGSettings>('/cms/settings/autorag');
  }

  async updateAutoRAGSettings(data: Partial<AutoRAGSettings>): Promise<APIResponse<AutoRAGSettings>> {
    return this.request<AutoRAGSettings>('/cms/settings/autorag', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Dashboard
  async getDashboard(): Promise<APIResponse<Dashboard>> {
    return this.request<Dashboard>('/cms/dashboard');
  }

  // Templates
  async getTemplates(): Promise<APIResponse<Template[]>> {
    return this.request<Template[]>('/cms/templates');
  }

  // Navigation
  async getNavigation(): Promise<APIResponse<NavigationItem[]>> {
    return this.request<NavigationItem[]>('/cms/navigation');
  }

  async updateNavigation(items: NavigationItem[]): Promise<APIResponse<NavigationItem[]>> {
    return this.request<NavigationItem[]>('/cms/navigation', {
      method: 'PUT',
      body: JSON.stringify({ items }),
    });
  }

  // AutoRAG Chat Integration
  async chatWithAutoRAG(question: string): Promise<APIResponse<{
    answer: string;
    sources: Array<{ file: string; page?: number; url?: string }>;
    cached?: boolean;
    processing_time?: number;
    provider: string;
  }>> {
    return this.request('/tool?tool=motomanual-chat', {
      method: 'POST',
      body: JSON.stringify({ question }),
    });
  }

  // Health check
  async healthCheck(): Promise<APIResponse<{ release: string }>> {
    return this.request('/health');
  }
}

// Export singleton instance
export const cmsAPI = new MotoManualCMSAPI();
export default cmsAPI;
